import { priceBlinkDuration, formatSpreadDiffStartLabel } from 'lib/constants';
import { withImplied, generateImpliedRows, patchMeetingSpreadEnd1, impliedSystemMid } from '@/helper/implied-pricing.helper';
import { MID_STATE, resolveMidRows, isLiveMidState } from '@/helper/clob-mid-state.helper';
// Zero-pad a price for display: outrights carry at least 3 decimals, spreads at
// least 1. Extra precision the price already has is kept (never truncated), and
// non-numeric / blank values pass through untouched.
export const padClobPrice = (value, isOutright) => {
    if (value === '' || value === null || value === undefined) return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    // Strip any leading '+' the source picked up (implied prices coming from
    // signed formatters upstream): only '-' should ever prefix the display.
    const raw = typeof value === 'string' ? value.replace(/^\+/, '') : String(value);
    const decimals = raw.split('.')[1]?.length || 0;
    return num.toFixed(Math.max(isOutright ? 3 : 1, decimals));
};

// Character widths that put the decimal point of every display price in a column
// on one axis: the widest integer part, and the widest fraction plus its dot.
export const decimalAlignWidths = (prices) => prices.reduce((widths, price) => {
    if (price === null || price === undefined || price === '') return widths;
    const [whole, fraction = ''] = String(price).split('.');
    return {
        intWidth: Math.max(widths.intWidth, whole.length),
        fracWidth: fraction ? Math.max(widths.fracWidth, fraction.length + 1) : widths.fracWidth
    };
}, { intWidth: 1, fracWidth: 0 });

// "5 Y" -> "5Y"; leaves the " x " / " → " leg separators alone.
export const stripTenorSpace = (label) => (typeof label === 'string'
    ? label.replace(/(\d)\s+([dwmy])(?![a-z])/gi, '$1$2')
    : label);

// Tenor units read as capitals in the grid (6m -> 6M, 1y x 3y -> 1Y x 3Y). The
// underlying labels stay lowercase — they key rows and build plot URLs — so this
// is display only.
export const upperTenorUnits = (label) => (typeof label === 'string'
    ? label.replace(/(\d)\s*([dwmy])(?![a-z])/g, (_, num, unit) => `${num}${unit.toUpperCase()}`)
    : label);

// Outright tenors read uppercase (6M); multi-leg structure tenors (spread/butterfly)
// stay lowercase (1y x 3y). buildTenor joins legs with " x ", so that separator marks
// a multi-leg tenor — the only ones the desk wants left lowercase.
export const displayTenor = (label) => (typeof label === 'string' && label.includes(' x ')
    ? stripTenorSpace(label)
    : upperTenorUnits(label));

// Sizes are carried in K throughout the CLOB, so display is the raw number with
// the suffix appended. Blank/non-numeric values render empty rather than "NaNK".
export const inK = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return `${Number(num.toFixed(1))}K`;
};

// Trim a submitted price to the decimal precision the backend accepts: outrights
// keep up to 5 places, spreads/butterflies (any structure carrying an `end1` leg)
// up to 3 — matching create-irs-price-validation. TRUNCATES the extra places (never
// rounds), so 5.55555 stores as 5.555, not 5.556, and the DB matches the grid.
// Non-numeric values pass through untouched.
export const trimClobPrice = (value, hasSpreadLeg) => {
    if (value === '' || value === null || value === undefined) return value;
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    const cap = hasSpreadLeg ? 3 : 5;
    const [int, frac = ''] = String(num).split('.');
    if (frac.length <= cap) return num;
    return Number(`${int}.${frac.slice(0, cap)}`);
};

// Bridges the lean irs-clob store shape (sections -> rows -> orderBook/midMatch)
// into the `securities` shape organisms/Clob/IrsOrderBookGrid consumes.
//
// Deliberately minimal: the API is display-trimmed for now, so price levels only
// carry what the cells render. The grid's cells degrade gracefully on missing
// fields (no Company -> no Bkr cell, no size -> none), so trading/iceberg/plot
// features stay dormant until the API is enriched — at which point the rich
// DesBondGridTanstack path takes over.

// The level is already the full price object (see backend toPriceLevel) — the
// item the cell + right-click menu read. Pass it through; resolve the order-book
// identity column per persona: a broker sees the trading party (users.code_name,
// carried as `tdr`), a trader sees the broker firm (short code `bkr`).
// IrsBrokerCell renders `displayCode` and tooltips `Company.name`.
const levelToEntry = (level, viewMode) => {
    // A trader sees the broker firm for every IDB price; a SELF price shows the
    // firm short code (BGC) to brokers too, so every broker reads it as that firm's
    // own price at a glance rather than a confusing trader code.
    const identity = (viewMode === 'trader' || level.isSelf)
        ? { code: level.bkr, name: level.bkrName, id: level.companyId }
        : { code: level.tdr, name: level.trader ? level.trader.name : level.tdr, id: level.trader ? level.trader.id : null };
    return { ...level, displayCode: identity.code, Company: { id: identity.id, name: identity.name } };
};

// Normalise a single midMatch entry to the shape buildIrsRows reads.
//
// Interest presence is dot-only (§4): a viewer sees their OWN size in Buy/Sell
// (myInterest), and a size-free dot when OTHERS hold interest. Trader entries
// carry myInterest/liveInterest booleans; broker entries carry restingCount
// (all resting participants) — both fold to the same liveInterest signal so the
// grid renders one presence dot regardless of persona. No count leaks.
// Fold the broker-view `participants` list into the MID INTEREST panel payload
// (entries + side totals). Broker snapshots carry participants; trader snapshots
// and delta `mu` updates do not, so return null and the hover stays quiet.
const buildInterest = (entry) => {
    const parts = entry.participants;
    if (!parts || !parts.length) return null;
    const entries = parts.map((p) => ({
        side: p.side === 'buy' ? 'Buy' : 'Sell',
        size: p.side === 'buy' ? (p.buy || 0) : (p.sell || 0),
        trader: p.trader || ''
    }));
    return {
        midPrice: entry.mid,
        broker: entry.bkr,
        entries,
        buyTotal: entries.filter((e) => e.side === 'Buy').reduce((s, e) => s + (Number(e.size) || 0), 0),
        sellTotal: entries.filter((e) => e.side === 'Sell').reduce((s, e) => s + (Number(e.size) || 0), 0)
    };
};

const midEntryToObject = (entry) => ({
    id: entry.id,
    buy: entry.buy,
    mid: entry.mid,
    sell: entry.sell,
    bkr: entry.bkr,
    companyId: entry.companyId,
    endsAt: entry.endsAt,
    iet: entry.iet ?? null,
    expired: Boolean(entry.expired),
    // Ended session: its last mid stays on the row until endedAt + the ended window.
    ended: Boolean(entry.ended),
    endedAt: entry.endedAt ?? null,
    startedAt: entry.startedAt ?? null,
    duration: entry.duration ?? null,
    interest: buildInterest(entry),
    // Derive from buy/sell directly rather than trust a server-set boolean.
    // The two must always agree, and drift here shows up as a stale green dot;
    // buy/sell are what the Buy/Sell columns already render, so the dot can't
    // disagree with the numbers.
    myInterest: (Number(entry.buy) || 0) > 0 || (Number(entry.sell) || 0) > 0,
    // Server sets liveInterest on both trader and broker paths ("someone else
    // holds interest, excluding self") — no restingCount fallback here or a
    // broker who's the only participant would flash their own dot.
    liveInterest: Boolean(entry.liveInterest),
    // Trade flash (see IrsMatchingGrid.PriceRenderer): tt is the trade time,
    // tInterest true while any trade has happened on the session — the grid
    // paints the row's Mid Match cells red for the 120s post-trade window.
    tt: entry.tt ?? null,
    tInterest: Boolean(entry.tInterest),
    // Broker-view resting participants ({ traderId, buy, sell, side, companyId })
    // from irs-clob-shape — IDB broker's grid resolves the selected trader chip's
    // interest off this array on initial load. Trader-view entries carry none.
    participants: entry.participants || []
});

// Whole stack (trader view: one per broker session on the structure). Broker
// view has 0 or 1 entries.
const midStack = (midMatch) => (midMatch || []).map(midEntryToObject);
const midToObject = (midMatch) => midStack(midMatch)[0];

// A Mid Match session is expired when the backend has ended it (End Session /
// FINISHED -> `expired` flag) OR its run duration has elapsed (endsAt <= now),
// mirroring isLivePrice for order-book prices. serverTime ticks each second, so
// a session greys within a second of its endsAt passing without any stop event.
export const isMidExpired = (mid, serverTime) => {
    if (!mid) return false;
    if (mid.expired) return true;
    if (mid.endsAt) {
        const now = new Date(serverTime || Date.now()).getTime();
        return new Date(mid.endsAt).getTime() <= now;
    }
    return false;
};

const rowToSecurity = (row, viewMode) => ({
    id: row.key,
    start: row.start,
    tenor: row.tenor,
    endLabel: row.endLabel,
    Bid: row.orderBook.bids.map((level) => levelToEntry(level, viewMode)),
    Offer: row.orderBook.offers.map((level) => levelToEntry(level, viewMode)),
    mid: midToObject(row.midMatch),
    // Full mid stack — trader view stacks one row per broker session on the
    // same structure; broker view has 0 or 1 entries (same as `mid`).
    midStack: midStack(row.midMatch),
    raw: row
});

// Sort a mixed real + synthetic row list back into the store's canonical
// section order: singletons before spreads, spreads before flies, then by
// near→mid→far leg maturity. Mirrors compareRows in irsClob.store.
const compareRows = (a, b) => {
    const spreadCmp = (a.start1Label || a.start1 ? 1 : 0) - (b.start1Label || b.start1 ? 1 : 0);
    if (spreadCmp !== 0) return spreadCmp;
    const flyCmp = (a.end2Label ? 1 : 0) - (b.end2Label ? 1 : 0);
    if (flyCmp !== 0) return flyCmp;
    // Meeting-date spreads carry both legs on start/start1 — no end1Label — and
    // their end1 is sometimes patched (patchMeetingSpreadEnd1) and sometimes
    // not, so leaning on end1 mixes patched real rows with unpatched implieds.
    // Sort meeting spreads purely by near/far start when neither row names an
    // end1 tenor; the tenor-spread path still uses end1 the normal way.
    const isMeetingSpread = !a.end1Label && !b.end1Label && (a.start1Label || b.start1Label);
    if (isMeetingSpread) {
        const nearCmp = String(a.end ?? a.endLabel ?? '').localeCompare(String(b.end ?? b.endLabel ?? ''));
        if (nearCmp !== 0) return nearCmp;
        return String(a.start1 ?? a.start1Label ?? '').localeCompare(String(b.start1 ?? b.start1Label ?? ''));
    }
    const nearCmp = String(a.end1 ?? a.end1Label ?? '').localeCompare(String(b.end1 ?? b.end1Label ?? ''));
    if (nearCmp !== 0) return nearCmp;
    const midCmp = String(a.end2 ?? a.end2Label ?? '').localeCompare(String(b.end2 ?? b.end2Label ?? ''));
    if (midCmp !== 0) return midCmp;
    return String(a.end ?? a.endLabel ?? '').localeCompare(String(b.end ?? b.endLabel ?? ''))
        || (a.nonDomestic ? 1 : 0) - (b.nonDomestic ? 1 : 0);
};

// Filter the raw store `impliedFlashes` object down to entries still inside
// the trade blink window. Returned as a Map so hot lookups (per-level) don't
// pay the Object hash cost.
export const activeImpliedFlashes = (impliedFlashes, serverTime) => {
    if (!impliedFlashes) return null;
    const now = new Date(serverTime || Date.now()).getTime();
    const out = new Map();
    Object.entries(impliedFlashes).forEach(([uid, entry]) => {
        if (!entry?.at) return;
        if (new Date(entry.at).getTime() + priceBlinkDuration <= now) return;
        out.set(uid, entry);
    });
    return out.size ? out : null;
};

// Walk an assembled row's orderBook and:
//   1. Cache every implied level (with a uid) into `lastSeen` so a later
//      ghost injection can rebuild it after the underlying legs vanish.
//   2. Tag levels whose uid appears in an active flash — priceFlash then
//      lights them for the remainder of the blink window.
//
// No side check: the uid (sort-join of child bond_price ids) uniquely
// identifies one implied on one side — a bid implied consumes different
// leg sides than an offer implied, so their id sets never collide. That
// also means we can ignore the leg-delta's own `side` (which is the
// leg's side, e.g. offer on a 2Y leg of a fly bid — a wrong signal for
// the implied's own side).
const tagAndCacheImplieds = (row, section, activeFlashes, lastSeen) => {
    if (!row?.orderBook) return row;
    const process = (sideName, arr) => (arr || []).map((level) => {
        if (!level?.implied || !level.impliedUid) return level;
        if (lastSeen) {
            lastSeen.set(level.impliedUid, {
                sectionId: section.id,
                row,
                level,
                side: sideName
            });
        }
        const entry = activeFlashes?.get(level.impliedUid);
        if (!entry) return level;
        return { ...level, traded: true, updatedAt: entry.at };
    });
    return {
        ...row,
        orderBook: {
            ...row.orderBook,
            bids: process('Bid', row.orderBook.bids),
            offers: process('Offer', row.orderBook.offers)
        }
    };
};

// For each active flash uid that no rendered row already carries, resurrect
// the last-seen row+level for one blink window so a full-fill implied still
// blinks before dropping off the grid. Viewers who never saw the implied
// (didn't have its curve open pre-trade) have no cached snapshot and see
// nothing — acceptable for the first pass; refresh flash is out of scope.
// Ghost side comes from the CACHED level (its own type), not the flash
// entry — leg deltas carry leg-side, which is the wrong signal here.
const buildGhostImpliedRows = (section, activeFlashes, lastSeen, seenUids) => {
    if (!activeFlashes || !lastSeen) return [];
    const ghosts = [];
    activeFlashes.forEach((entry, uid) => {
        if (seenUids.has(uid)) return;
        const cached = lastSeen.get(uid);
        if (!cached || cached.sectionId !== section.id) return;
        const ghostLevel = { ...cached.level, traded: true, updatedAt: entry.at };
        ghosts.push({
            ...cached.row,
            orderBook: {
                bids: cached.side === 'Bid' ? [ghostLevel] : [],
                offers: cached.side === 'Offer' ? [ghostLevel] : []
            }
        });
    });
    return ghosts;
};

// Forget this section's cached implieds that are neither on screen nor
// inside a blink window, so the session-long cache stays the size of the grid.
const pruneLastSeenImplieds = (lastSeen, section, seenUids, activeFlashes) => {
    if (!lastSeen) return;
    lastSeen.forEach((cached, uid) => {
        if (cached.sectionId === section.id && !seenUids.has(uid) && !activeFlashes?.has(uid)) lastSeen.delete(uid);
    });
};

const collectSeenImpliedUids = (rows) => {
    const seen = new Set();
    rows.forEach((row) => {
        [...(row.orderBook?.bids || []), ...(row.orderBook?.offers || [])].forEach((l) => {
            if (l?.implied && l.impliedUid) seen.add(l.impliedUid);
        });
    });
    return seen;
};

// Path A implied levels: splice synthetic IMP levels into every existing row AND surface
// synthetic rows for candidate structures that have no store entry yet. Never mutates the
// store row. Reads only the section's order books, so its rows can be reused while just a
// System mid or a mid session moves — see withLiveRowFields.
// See docs/requirements/clob-implied-pricing.
export const pricedSectionRows = (section, impliedCtx) => {
    // Meeting-spread rows ship with `end1: null`; recover it from the far
    // period's outright `end` (which IS the meeting after the far period)
    // before withImplied runs, so the plot URL and price form see a
    // complete row. Applies to real and synthetic rows uniformly.
    const patch = section.structureType === 'meetingDates'
        ? (row) => patchMeetingSpreadEnd1(row, impliedCtx.outrightRowsByType)
        : (row) => row;
    const existing = section.rowOrder.map((key) => (
        withImplied(patch(section.rows[key]), section.structureType, impliedCtx.outrightBooksByType, impliedCtx)
    ));
    const generated = generateImpliedRows(section, impliedCtx.outrightBooksByType, impliedCtx);
    const combined = generated.length ? [...existing, ...generated].sort(compareRows) : existing;
    const activeFlashes = activeImpliedFlashes(impliedCtx.impliedFlashes, impliedCtx.serverTime);
    const lastSeen = impliedCtx.lastSeenImplied || null;
    const decorated = combined.map((row) => tagAndCacheImplieds(row, section, activeFlashes, lastSeen));
    const seenUids = collectSeenImpliedUids(decorated);
    const ghosts = buildGhostImpliedRows(section, activeFlashes, lastSeen, seenUids);
    pruneLastSeenImplieds(lastSeen, section, seenUids, activeFlashes);
    // Splice ghost levels into the existing structure row when one is
    // already rendered — otherwise the same structure shows twice (real
    // row + ghost row) during the flash window. Ghost stands alone only
    // when no host row exists (fully synthetic implied that vanished).
    let merged = decorated;
    const standalone = [];
    if (ghosts.length) {
        const ixByKey = new Map();
        merged = decorated.slice();
        merged.forEach((r, i) => { if (r?.key) ixByKey.set(r.key, i); });
        ghosts.forEach((g) => {
            const ix = g?.key != null ? ixByKey.get(g.key) : undefined;
            if (ix === undefined) { standalone.push(g); return; }
            const host = merged[ix];
            merged[ix] = {
                ...host,
                orderBook: {
                    ...host.orderBook,
                    bids: [...(host.orderBook?.bids || []), ...(g.orderBook?.bids || [])],
                    offers: [...(host.orderBook?.offers || []), ...(g.orderBook?.offers || [])]
                }
            };
        });
    }
    return standalone.length ? [...merged, ...standalone].sort(compareRows) : merged;
};

// A priced row as the store holds it now: System mid and mid sessions change without a price,
// so they are read off the live store row. A synthetic row has no store row — its System mid
// comes from its legs' (its outright template can carry that outright's own).
const withLiveRowFields = (row, section, outrightRowsByType, outrightsInBp) => {
    if (!row) return row;
    const stored = row.key != null ? section.rows[row.key] : undefined;
    if (!stored) return { ...row, systemMid: impliedSystemMid(row, section.structureType, outrightRowsByType, outrightsInBp) };
    if (stored.systemMid === row.systemMid && stored.systemMidAt === row.systemMidAt && stored.midMatch === row.midMatch) return row;
    return { ...row, systemMid: stored.systemMid, systemMidAt: stored.systemMidAt, midMatch: stored.midMatch };
};

// `outrightRowsByType` is the curve's outright rows as they are now (buildOutrightRowsByType).
export const pricedRowsToSecurities = (rows, section, viewMode, { outrightRowsByType, outrightsInBp }) => rows
    .map((row) => rowToSecurity(withLiveRowFields(row, section, outrightRowsByType, outrightsInBp), viewMode));

export const sectionToSecurities = (section, viewMode = 'broker', impliedCtx = null) => {
    if (!section) return [];
    if (!impliedCtx?.outrightBooksByType) return section.rowOrder.map((key) => rowToSecurity(section.rows[key], viewMode));
    return pricedRowsToSecurities(pricedSectionRows(section, impliedCtx), section, viewMode, impliedCtx);
};

// Bulk-cancel helper: flatten midMatch ids off a list of store rows. Used by the
// Mid right-click "Cancel All Interest In …" actions (broker-only).
// Only live sessions: an ended session's interest is already gone, so a bulk cancel
// neither sends it nor counts it.
const liveMidMatch = (row) => (row?.midMatch || []).filter((m) => m && !isMidExpired(m));

export const collectMidMatchIds = (rows) => (rows || [])
    .flatMap((row) => liveMidMatch(row).map((m) => m.id))
    .filter(Boolean);

// Companion to collectMidMatchIds: how many interests (one per trader per side)
// a bulk cancel clears across the same rows. The cancel zeroes every participant
// on the session, so count the broker-view `participants`, falling back to the
// entry's own buy/sell when none were shipped.
const restingSides = (m) => ((Number(m?.buy) || 0) > 0 ? 1 : 0) + ((Number(m?.sell) || 0) > 0 ? 1 : 0);

export const countMidInterests = (rows) => (rows || [])
    .flatMap(liveMidMatch)
    .reduce((total, m) => total + (m.participants?.length
        ? m.participants.reduce((sum, p) => sum + restingSides(p), 0)
        : restingSides(m)), 0);

// Toast copy for the Mid menu's three bulk cancels. The scope decides how wide
// the message reads: one match names the structure it sat on, a section names
// curve + section, a curve names just the curve. `mids` comes off the API's
// affected rows, `interests` off the grid — they answer different questions
// ("how many sessions did we touch" vs "how much of my book went").
export const cancelInterestMessage = ({ scope, interests, mids, structureLabel, sectionLabel, curveLabel } = {}) => {
    if (!interests) return 'No resting interest to cancel';
    const lead = `${interests} Interest(s) Cancelled`;
    if (scope === 'match') return structureLabel ? `${lead} on ${structureLabel}` : lead;
    const where = scope === 'section'
        ? [curveLabel, sectionLabel].filter(Boolean).join(' - ')
        : curveLabel;
    const across = `${lead} Across ${mids} ${mids === 1 ? 'Mid' : 'Mids'}`;
    return where ? `${across} in ${where}` : across;
};

// ---- Display filters (UI-side; the snapshot carries everything) --------------
//
// Every filtered section runs two axes — scope (Standard | All) × liveness
// (Live only | All) — persisted per user as one slug and cycled in this order.

export const FILTER_SLUGS = ['std-live', 'std-all', 'all-live', 'all-all'];
export const DEFAULT_FILTER_SLUG = 'std-all';

const SLUG_TO_FILTER = {
    'std-live': { tenor: 'standard', live: 'live' },
    'std-all': { tenor: 'standard', live: 'all' },
    'all-live': { tenor: 'all', live: 'live' },
    'all-all': { tenor: 'all', live: 'all' }
};

const SLUG_LABEL = {
    'std-live': 'Standard · live only',
    'std-all': 'Standard · all prices',
    'all-live': 'All · live only',
    'all-all': 'All · all prices'
};

export const filterForSlug = (slug) => SLUG_TO_FILTER[slug] || SLUG_TO_FILTER[DEFAULT_FILTER_SLUG];
export const labelForSlug = (slug) => SLUG_LABEL[slug] || SLUG_LABEL[DEFAULT_FILTER_SLUG];
export const isLiveOnlySlug = (slug) => filterForSlug(slug).live === 'live';
export const nextFilterSlug = (slug) => {
    const idx = FILTER_SLUGS.indexOf(slug);
    return FILTER_SLUGS[(idx + 1) % FILTER_SLUGS.length];
};

// Reverse of filterForSlug: build a slug from its two axes.
const FILTER_TO_SLUG = {
    'standard|live': 'std-live',
    'standard|all': 'std-all',
    'all|live': 'all-live',
    'all|all': 'all-all'
};
export const slugFor = ({ tenor, live }) => FILTER_TO_SLUG[`${tenor}|${live}`] || DEFAULT_FILTER_SLUG;

// Flip only the liveness axis of a slug, keeping its tenor scope — the "Live
// Prices Only" checkbox drives this so it never disturbs a section's Standard/All
// scope (std-all↔std-live, all-all↔all-live).
export const withLiveAxis = (slug, live) => slugFor({ tenor: filterForSlug(slug).tenor, live });

// A CCIL feed level (outright or CCIL-derived implied) is view-only, live or stale.
export const isCcilFeedLevel = (item) => item?.source === 'CCIL';

// Whether a /cancel-irs-price removal belongs on this market's grid. CCIL feed
// prices are AGENCY-persisted but shown on both markets, so their removals pass.
export const isRemovalForMarket = (delta, brokerType) => Boolean(delta)
    && (delta.source === 'CCIL' || delta.brokerType === brokerType);

// A price level is live unless it has expired (past its expiresAt), traded down
// to zero size, or is a deleted price — mirrors hooks/usePriceDisabled.
export const isLivePrice = (item, serverTime, isDeletedPrices = false) => {
    if (!item) return false;
    if (isDeletedPrices) return false;
    if (item.traded && Number(item.dv01 ?? item.size) === 0) return false;
    if (item.expiresAt) {
        const now = new Date(item.serverTime || serverTime || Date.now()).getTime();
        if (new Date(item.expiresAt).getTime() <= now) return false;
    }
    return true;
};

// Whether the level a Tdr popup was opened on still rests live on the book —
// false once it is cancelled, fully traded or expired.
export const isLevelStillLive = (securities = [], levelId, serverTime) => {
    const level = securities
        .flatMap((security) => [...(security?.Bid || []), ...(security?.Offer || [])])
        .find((item) => item?.id === levelId);
    return isLivePrice(level, serverTime);
};

// Total LIVE prices across a curve's sections — the curve-header count. Counts
// live order-book levels PLUS live (non-expired) mid sessions; view-only CCIL feed
// prices never count, live or stale. Filter-independent
// (ignores each section's Standard/All + live/all state); read straight off the
// store rows, so it tracks price + matching deltas as they stream in.
export const curveLivePriceCount = (sections, serverTime) => sections.reduce((total, section) => total
    + section.rowOrder.reduce((rowTotal, key) => {
        const { orderBook, midMatch } = section.rows[key];
        return rowTotal
            + orderBook.bids.filter((level) => isLivePrice(level, serverTime) && !isCcilFeedLevel(level)).length
            + orderBook.offers.filter((level) => isLivePrice(level, serverTime) && !isCcilFeedLevel(level)).length
            + midMatch.filter((entry) => !isMidExpired(entry, serverTime)).length;
    }, 0), 0);

// Total prices on screen for one section under its active filter — the
// section-header count. Bid/Offer + the mid are already trimmed to the live set
// under the live filter, so this counts live there and live+expired under "all".
export const filteredPriceCount = (securities) => securities
    .reduce((total, sec) => total + sec.Bid.length + sec.Offer.length + (sec.mid ? 1 : 0), 0);

// A level flashes for the blink window after it last changed — the same rule DES
// runs (hooks/usePriceBlink): `priceBlinkDuration` measured off `updatedAt`. A
// traded level flashes its side's trade colour, an untraded one the "new" colour.
export const priceFlash = (item, serverTime) => {
    if (!item?.updatedAt) return null;
    const now = new Date(serverTime || Date.now()).getTime();
    if (new Date(item.updatedAt).getTime() + priceBlinkDuration <= now) return null;
    if (item.traded) return item.type === 'Offer' ? 'lift' : 'hit';
    return 'new';
};

// A Mid Match session paints a post-trade flash for 120s after its trade time —
// a dealt row counts as live through that window even once its price is gone.
const MATCH_FLASH_MS = 120 * 1000;
const isMidTradeFlashing = (mid, serverTime) => {
    if (!mid?.tInterest || !mid?.tt) return false;
    const now = new Date(serverTime || Date.now()).getTime();
    return new Date(mid.tt).getTime() + MATCH_FLASH_MS > now;
};

// A price level keeps its place under the live filter while it is live OR still
// flashing — a dealt level rests through its fill flash, then drops.
const isLiveOrFlashing = (item, serverTime, isDeletedPrices) => (
    isLivePrice(item, serverTime, isDeletedPrices) || Boolean(priceFlash(item, serverTime))
);
const secHasLiveContent = (sec, serverTime, isDeletedPrices) => (
    (sec.Bid || []).some((item) => isLiveOrFlashing(item, serverTime, isDeletedPrices))
    || (sec.Offer || []).some((item) => isLiveOrFlashing(item, serverTime, isDeletedPrices))
    || Boolean(sec.mid && (!isMidExpired(sec.mid, serverTime) || isMidTradeFlashing(sec.mid, serverTime)))
);

// A fully-dealt level is gone the moment its fill flash settles: it rests at zero
// size, which is not a price but a blank-size ghost row. The backend keeps the
// zeroed row (traded/size 0) and streams it as an upsert — no removal — so the
// grid must drop it itself. Unlike the live filter this is filter-independent:
// even "all prices" (which keeps expired/history) must not show a traded-out
// level, only its fill flash while it lasts. Cancelled-price grids are exempt —
// there the dealt levels are the content.
export const isDeadDealtLevel = (item, serverTime) => Boolean(
    item?.traded
    && Number(item.dv01 ?? item.size) === 0
    && !priceFlash(item, serverTime)
);

export const stripDeadDealtPrices = (securities, serverTime, isDeletedPrices = false) => {
    if (isDeletedPrices) return securities;
    return securities.map((sec) => {
        const Bid = (sec.Bid || []).filter((item) => !isDeadDealtLevel(item, serverTime));
        const Offer = (sec.Offer || []).filter((item) => !isDeadDealtLevel(item, serverTime));
        if (Bid.length === (sec.Bid || []).length && Offer.length === (sec.Offer || []).length) return sec;
        return { ...sec, Bid, Offer };
    });
};

// Trim a section's securities for the active filter. Standard scope keeps only
// the curve's standard structures; under the live axis a structure with nothing
// live (no live/flashing bid, offer, or mid session) is dropped — empty standard
// rows included, so `standard · live` shows only what is trading.
// keepIds: structures held on screen under a live scope although nothing live is
// left on them (see prunePullHolds).
export const filterSecurities = (securities, { tenor, live }, { serverTime, isDeletedPrices, keepIds } = {}) => {
    let result = securities;
    if (tenor === 'standard') result = result.filter((sec) => sec.raw?.standard);
    if (live === 'live') {
        result = result
            .map((sec) => ({
                ...sec,
                Bid: sec.Bid.filter((item) => isLiveOrFlashing(item, serverTime, isDeletedPrices)),
                Offer: sec.Offer.filter((item) => isLiveOrFlashing(item, serverTime, isDeletedPrices)),
                mid: sec.mid && (!isMidExpired(sec.mid, serverTime) || isMidTradeFlashing(sec.mid, serverTime))
                    ? sec.mid : undefined
            }))
            .filter((sec) => sec.Bid.length || sec.Offer.length || sec.mid || keepIds?.has(String(sec.id)));
    }
    return result;
};

export const PULL_HOLD_MS = 20000;

const otherLivePrices = (security, priceId, serverTime) => [...(security?.Bid || []), ...(security?.Offer || [])]
    .filter((item) => item.id !== priceId && isLiveOrFlashing(item, serverTime));

// Under any scope, pulling a structure's last live price holds its emptied row
// on screen instead of folding it at once.
export const isLastLivePrice = (security, item, serverTime) => Boolean(item?.id)
    && isLiveOrFlashing(item, serverTime)
    && otherLivePrices(security, item.id, serverTime).length === 0
    && !(security?.mid && !isMidExpired(security.mid, serverTime));

// holds: { [securityId]: { priceId, pulledAt } }. A hold lasts PULL_HOLD_MS, or
// longer while that structure's cell is marked, and ends early once a new live
// price lands on it. Returns the same object when nothing changed.
export const prunePullHolds = (holds, securities, { serverTime, markedSecurityId } = {}) => {
    const now = serverTime ?? Date.now();
    const kept = Object.entries(holds).filter(([securityId, hold]) => {
        const security = securities.find((sec) => String(sec.id) === securityId);
        if (security && otherLivePrices(security, hold.priceId, serverTime).length) return false;
        return securityId === String(markedSecurityId) || now - hold.pulledAt < PULL_HOLD_MS;
    });
    return kept.length === Object.keys(holds).length ? holds : Object.fromEntries(kept);
};

// A non-standard row leaves the store with its last price; a held one is put back
// from the copy taken at the pull.
export const withHeldRows = (securities, holds) => {
    const ids = new Set(securities.map((sec) => String(sec.id)));
    const missing = Object.entries(holds)
        .filter(([id, hold]) => hold.security && !ids.has(id))
        .map(([, hold]) => hold.security);
    return missing.length ? [...securities, ...missing].sort((a, b) => compareRows(a.raw, b.raw)) : securities;
};

// Arrow-key move across every grid on the board, read off where the cells sit on
// screen: the nearest cell that way, preferring one in the same column (Up/Down)
// or row (Left/Right). rects are { left, right, top, bottom }; returns an index or -1.
export const nextCellInDirection = (rects, current, direction) => {
    const vertical = direction === 'ArrowUp' || direction === 'ArrowDown';
    const sign = direction === 'ArrowDown' || direction === 'ArrowRight' ? 1 : -1;
    const [start, crossStart, crossEnd] = vertical ? ['top', 'left', 'right'] : ['left', 'top', 'bottom'];
    const crossMid = (rect) => (rect[crossStart] + rect[crossEnd]) / 2;
    const scored = rects
        .map((rect, index) => ({
            index,
            ahead: sign * (rect[start] - current[start]),
            inLine: rect[crossStart] < current[crossEnd] - 1 && rect[crossEnd] > current[crossStart] + 1,
            drift: Math.abs(crossMid(rect) - crossMid(current))
        }))
        .filter((cell) => cell.ahead > 1)
        .sort((a, b) => (b.inLine - a.inLine) || (a.ahead - b.ahead) || (a.drift - b.drift));
    return scored.length ? scored[0].index : -1;
};

// Under a Standard scope, how many live non-standard structures the filter hides
// — the section header states this so a broker knows there is more off-screen.
// What a filtered sub-pane shows: dealt-out ghosts dropped, then its filter
// applied. Shared by the grid and the curve copy so the two cannot drift.
// holds: the grid's pull holds (see prunePullHolds); the copy passes none.
export const shownSecurities = (securities, filter, { serverTime, isDeletedPrices, holds = {} } = {}) => {
    const base = stripDeadDealtPrices(securities, serverTime, isDeletedPrices);
    return filterSecurities(
        withHeldRows(base, holds),
        filter,
        { serverTime, isDeletedPrices, keepIds: new Set(Object.keys(holds)) }
    );
};

export const hiddenLiveNonStandardCount = (securities, serverTime, isDeletedPrices = false) => securities
    .filter((sec) => !sec.raw?.standard && secHasLiveContent(sec, serverTime, isDeletedPrices))
    .length;

// Section display order within a curve, keyed by section.structureType (from the
// backend payload). outright, spread & butterfly, fwd, meeting dates, ..., custom.
export const SECTION_TYPE_ORDER = {
    outright: 0,
    spreadFly: 1,
    audDcSpreadFly: 2,
    fwd: 3,
    fwdSpreadFly: 4,
    meetingDates: 5,
    meetingDatesSpread: 6,
    bob: 7,
    bobFwd: 8,
    bobSpreadFly: 9,
    bobCustom: 10,
    sixs: 11,
    sixsFwd: 12,
    sixsSpreadFly: 13,
    sixsCustom: 14,
    custom: 15
};

const sectionRank = (type) => (SECTION_TYPE_ORDER[type] ?? Number.MAX_SAFE_INTEGER);

// Sections that collapse Start + Tenor into one Structure column. Meeting-dates
// carry a backend structureLabel; custom (different-start-date) build it UI-side.
export const MEETING_DATE_SECTIONS = new Set(['meetingDates', 'meetingDatesSpread']);
export const CUSTOM_SECTIONS = new Set(['custom', 'bobCustom', 'sixsCustom']);
export const isMeetingDateSection = (type) => MEETING_DATE_SECTIONS.has(type);
export const isCustomSection = (type) => CUSTOM_SECTIONS.has(type);

// fwdSpreadFly is both: a forward-start structure AND a spread/butterfly, so it
// carries the Start column (forward) and the spread legs (spread).
const FORWARD_TYPES = new Set(['fwd', 'fwdSpreadFly', 'bobFwd', 'sixsFwd']);
const SPREAD_TYPES = new Set([
    'spreadFly', 'fwdSpreadFly', 'meetingDatesSpread', 'audDcSpreadFly',
    'bobSpreadFly', 'bobCustom', 'sixsSpreadFly', 'sixsCustom', 'custom'
]);

// Synthesise the sector shape IrsOrderBookGrid + buildSectorContext read, from
// store section meta. Replaces the userSectors-derived `sector` so the grid is a
// pure reader of the irs-clob store (no /user-sectors-bonds dependency).
export const sectorFromSection = (section) => ({
    isIrs: true,
    isCurrency: false,
    isCds: false,
    isFxOption: false,
    isCustomIrs: section.isCustom || false,
    forward: FORWARD_TYPES.has(section.structureType),
    spread: SPREAD_TYPES.has(section.structureType),
    structureType: section.structureType,
    Sector: {
        id: section.irsId,
        name: section.curveName,
        currency: section.currency,
        currencies: [section.currency],
        settlement: section.settlement,
        IrsSecurities: (section.terms || []).map((term) => ({ term })),
        // MarketTimeInfo: mktOpen/CloseUtc drive the open/closed calc; the raw
        // marketOpen/Close (HH:mm:ss) + region are what it displays.
        marketOpen: section.marketOpen,
        marketClose: section.marketClose,
        region: section.region,
        mktOpenUtc: section.mktOpenUtc,
        mktCloseUtc: section.mktCloseUtc
    }
});

// Base curve name for ordering — mirrors the backend snapshot's getBaseName so
// the grid's curve columns sort the same whether a curve arrived in the snapshot
// or was spliced in later (LHS add / streamed price). Strips the structure-suffix
// a curveName can carry so "AUD BOB - Spread and Butterfly" sorts as "AUD BOB".
const baseCurveName = (name = '') => name
    .replace(/\s*-\s*Spread and Butterfly$/i, '')
    .replace(/\s+FWD START\/FRAs$/i, '')
    .replace(/\s*-\s*Custom$/i, '')
    .trim();

// Curve columns from the store: one block per irsId, sections stacked in
// structure-type order, the columns themselves ordered by base curve name
// (alpha) — the SAME key the snapshot's sortSections uses — so an added curve
// slots into its place instead of appending at the end.
// Curve columns follow the LHS sidebar order — the user's favourite order
// (UserIrs.sortOrder), passed in as `curveOrder` (irsIds). A curve not in that
// list (enabled but not favourited) sorts after the ordered ones, alphabetically
// by base name, so the grid stays deterministic.
// Synthesize an empty custom section for every fwd variant a curve carries
// but that has no backend-emitted custom section yet. The custom section only
// exists once a user prices a custom row, so without this the Path A implied-
// custom rows (`generateImpliedRows` for `custom` / `bobCustom` / `sixsCustom`)
// have no grid to render into. The synthetic section mirrors the fwd
// section's curve identity so downstream (market hours, curve name, etc.)
// keeps working — only rowOrder is empty; `sectionToSecurities` fills it.
const CUSTOM_FOR_FWD = { fwd: 'custom', bobFwd: 'bobCustom', sixsFwd: 'sixsCustom' };
const CUSTOM_LABEL_SUFFIX = { custom: 'Custom Structures', bobCustom: 'BOB Custom Structures', sixsCustom: '6s3s Custom Structures' };
// Same idea as custom-for-fwd: for each outright variant a curve carries, also
// synthesise an empty spread/fly section when the backend hasn't emitted one.
// generateImpliedRows then fills it with outright+outright implieds. An empty
// standardList (no curated tenors) means only rows with an actual derived
// implied survive — so the synthesised section renders nothing when no
// implieds exist, and the caller drops the empty grid.
const SPREAD_FOR_OUTRIGHT = { outright: 'spreadFly', bob: 'bobSpreadFly', sixs: 'sixsSpreadFly' };
const SPREAD_LABEL_SUFFIX = {
    spreadFly: 'Spreads & Flies',
    bobSpreadFly: 'BOB Spreads & Flies',
    sixsSpreadFly: '6s3s Spreads & Flies'
};
// A generated section is reused while its source section is unchanged, so a curve whose
// sections did not change keeps handing its grids the same section objects.
const generatedSections = new WeakMap();
const generatedFrom = (src, structureType, build) => {
    const byType = generatedSections.get(src) || {};
    if (!byType[structureType]) {
        byType[structureType] = build();
        generatedSections.set(src, byType);
    }
    return byType[structureType];
};

// Shared by every generated section, so rebuilding one for a System mid change on its source
// hands implied pricing the same (empty) inputs and does not reprice the curve.
const NO_ROWS = Object.freeze([]);
const NO_STANDARD_LIST = Object.freeze({ coreSpreads: NO_ROWS, coreFlies: NO_ROWS });

const ensureCustomSections = (groupSections) => {
    const present = new Set(groupSections.map((s) => s.structureType));
    const extras = [];
    groupSections.forEach((src) => {
        const customType = CUSTOM_FOR_FWD[src.structureType];
        if (customType && !present.has(customType)) {
            present.add(customType);
            extras.push(generatedFrom(src, customType, () => ({
                ...src,
                id: `${src.irsId}_${customType}`,
                structureType: customType,
                label: `${baseCurveName(src.curveName || '')} ${CUSTOM_LABEL_SUFFIX[customType]}`.trim(),
                curveName: src.curveName,
                rowOrder: NO_ROWS,
                rows: {}
            })));
        }
        const spreadType = SPREAD_FOR_OUTRIGHT[src.structureType];
        if (spreadType && !present.has(spreadType)) {
            present.add(spreadType);
            extras.push(generatedFrom(src, spreadType, () => ({
                ...src,
                id: `${src.irsId}_${spreadType}`,
                structureType: spreadType,
                label: `${baseCurveName(src.curveName || '')} ${SPREAD_LABEL_SUFFIX[spreadType]}`.trim(),
                curveName: src.curveName,
                rowOrder: NO_ROWS,
                rows: {},
                standardList: NO_STANDARD_LIST,
                synthesized: true
            })));
        }
    });
    return extras.length ? [...groupSections, ...extras] : groupSections;
};

// AUD OIS traders work the RBA meeting dates first, so its Meeting Dates pane leads.
const MEETING_FIRST_CURVES = new Set(['AUD OIS']);
const sectionRankFor = (curveName, type) => (
    MEETING_FIRST_CURVES.has(curveName) && type === 'meetingDates' ? -1 : sectionRank(type)
);

export const buildCurveGroups = (sections, sectionOrder, curveOrder = [], allowedIrsIds = null) => {
    const allowed = allowedIrsIds && new Set(allowedIrsIds);
    const byCurve = new Map();
    sectionOrder.forEach((id) => {
        const section = sections[id];
        if (!section) return;
        if (allowed && !allowed.has(section.irsId)) return;
        // A curve hosting the BOB / 6s3s books shows each book as its own curve (`curveGroupId`).
        const key = section.curveGroupId || section.irsId;
        if (!byCurve.has(key)) {
            byCurve.set(key, { key, irsId: section.irsId, title: section.curveName, count: 0, live: false, sections: [] });
        }
        const group = byCurve.get(key);
        const rowCount = section.rowOrder.length;
        group.sections.push(section);
        group.count += rowCount;
        group.live = group.live || rowCount > 0;
    });
    const rankByIrsId = new Map(curveOrder.map((irsId, index) => [irsId, index]));
    const rankOf = (irsId) => (rankByIrsId.has(irsId) ? rankByIrsId.get(irsId) : Infinity);
    return [...byCurve.values()]
        .map((group) => ({
            ...group,
            sections: ensureCustomSections(group.sections).sort((a, b) => sectionRankFor(group.title, a.structureType) - sectionRankFor(group.title, b.structureType))
        }))
        .sort((a, b) => {
            // Both unranked -> Infinity - Infinity = NaN; fall through to name sort.
            const rankDiff = rankOf(a.irsId) - rankOf(b.irsId);
            if (rankDiff && !Number.isNaN(rankDiff)) return rankDiff;
            if (a.irsId === b.irsId) return sectionRank(a.sections[0].structureType) - sectionRank(b.sections[0].structureType);
            return baseCurveName(a.title).toLowerCase().localeCompare(baseCurveName(b.title).toLowerCase())
                || a.title.localeCompare(b.title);
        });
};

// Horizontal scroll offset that puts `column` flush at the left (start) of the
// scrolling `container`. Summed up the offsetParent chain to the container: the
// column's absolute x within the scroll content, i.e. exactly the scrollLeft
// that pins it to the start. Layout/animation independent — unlike live rects +
// container.scrollLeft, which drift when read mid smooth-scroll or under the
// flex-wrap column layout, leaving the column short of the edge.
export const scrollLeftForColumn = (container, column) => {
    if (!container || !column) return null;
    let left = 0;
    let node = column;
    while (node && node !== container) {
        left += node.offsetLeft;
        node = node.offsetParent;
    }
    // offsetParent chain didn't reach the container — fall back to rect math.
    if (node !== container) {
        left = column.getBoundingClientRect().left
            - container.getBoundingClientRect().left
            + container.scrollLeft;
    }
    return Math.max(0, left);
};

// Own price. A trader owns their own level; a broker owns anything resting on
// their firm — the rule the right-click menu's 'own' variant runs on, shared so
// the grid's cells (iceberg cancel) and the menu can never disagree.
export const isOwnPrice = (item, auth) => {
    if (!item || !auth?.id) return false;
    // CCIL / auto-broker feed prices are system-generated; the configured auto
    // broker's firm must not see them as their own.
    if (item.source === 'CCIL') return false;
    if (auth.role === 'Broker') {
        return (!item.traderId && item.userId === auth.id) || item.companyId === auth.companyId;
    }
    return item.traderId === auth.id || (!item.traderId && item.userId === auth.id);
};

// A level the *viewer* placed personally, as opposed to isOwnPrice above, which
// hands a broker everything resting on their firm. The own-price tint keys off this
// so it never lights on a colleague's price.
export const isViewerLevel = (item, auth) => Boolean(item && auth?.id
    && !(item.source === 'CCIL')
    && (item.traderId === auth.id || (!item.traderId && item.userId === auth.id)));

// A SELF price is the firm's own book: every broker at the submitting firm gets the
// own-firm yellow highlight — not only whoever placed it — so BGC brokers all see a
// BGC self price lit. Only self prices firm-tint; a colleague's ordinary price still
// stays dark (see isViewerLevel).
export const isSelfFirmLevel = (item, auth) => Boolean(item && item.isSelf
    && auth?.role === 'Broker' && auth?.companyId
    && item.companyId === auth.companyId);

// Row-level flash the grid paints. Every flash is one-sided: a print tints the side
// that was hit/lifted (and carries the accent bar there), a new price tints the side
// it landed on. When both sides print inside the window, the later trade owns the row.
export const rowFlash = (row, serverTime) => {
    const bid = priceFlash(row?.bidItem, serverTime);
    const offer = priceFlash(row?.offerItem, serverTime);
    if (!bid && !offer) return null;
    if (bid === 'hit' || offer === 'lift') {
        // Both sides printed inside the window — the later trade owns the row.
        const side = (bid === 'hit' && offer === 'lift')
            ? (new Date(row.bidItem.updatedAt).getTime() >= new Date(row.offerItem.updatedAt).getTime() ? 'Bid' : 'Offer')
            : (bid === 'hit' ? 'Bid' : 'Offer');
        return { kind: side === 'Bid' ? 'hit' : 'lift', tint: [side], edge: [side] };
    }
    const sides = [bid && 'Bid', offer && 'Offer'].filter(Boolean);
    return { kind: 'new', tint: sides, edge: sides };
};

// Sidebar nav items from the store curve groups (shape ClobSwapsSidebar reads).
export const buildNavItems = (curveGroups) => curveGroups.map((g) => ({ id: g.key, label: g.title, count: g.count, live: g.live }));

// --- Legacy (dashboard.js): userSectors-driven section lookup. Clob.js no
// longer uses these; retained until dashboard.js is retired. ---
const sectionKeyForSector = (sector) => {
    if (sector.isAudDomesticConvention) return 'audDcSpreadFly';
    if (sector.meetingDates) return 'meetingDates';
    if (sector.isIrsBob) {
        if (sector.diffStartDate) return 'bobCustom';
        if (sector.spread) return 'bobSpreadFly';
        return sector.forward ? 'bobFwd' : 'bob';
    }
    if (sector.isIrs6s3s) {
        if (sector.diffStartDate) return 'sixsCustom';
        if (sector.spread) return 'sixsSpreadFly';
        return sector.forward ? 'sixsFwd' : 'sixs';
    }
    if (sector.diffStartDate) return 'custom';
    if (sector.spread) return sector.forward ? 'fwdSpreadFly' : 'spreadFly';
    return sector.forward ? 'fwd' : 'outright';
};

export const sectionIdForSector = (sector) => `irs_${sector.Sector.id}_${sectionKeyForSector(sector)}`;

export const compareSectorsByType = (a, b) => sectionRank(sectionKeyForSector(a))
    - sectionRank(sectionKeyForSector(b));

// Custom (different-start-date) sections drop the separate Start + Tenor columns
// for one "Structure" column, e.g. "1w-6m X spot-1w". Reuses the DES name-renderer
// formatter (formatSpreadDiffStartLabel) off the row's raw leg labels so the CLOB
// label matches DesBondGrid exactly.
export const customStructureLabel = (raw = {}) => {
    if (raw?.start1Label || raw?.end1Label) {
        return formatSpreadDiffStartLabel(raw.startLabel, raw.endLabel, raw.start1Label, raw.end1Label);
    }
    // Imm rolls: the roll (startLabel → immLabel) then the tenor(s), e.g.
    // "M7 → U7  1y x 3y x 5y". IMM codes upper, tenor lower — same casing as the
    // DES custom grid. Plain-text form for the context menu; the grid cell renders
    // the same with the arrow icon (immRollStructure).
    if (raw?.imm || raw?.immLabel) {
        const up = (v = '') => v.toUpperCase().replace(/\s+/g, '');
        const roll = `${up(raw.startLabel)}${raw.immLabel ? ` → ${up(raw.immLabel)}` : ''}`;
        return [roll, raw.tenor].filter(Boolean).join('  ');
    }
    return '';
};

// Meeting-date Structure label: backend structureLabel (date legs + Full Dt/Stub
// variant) with a fallback to the plain start identity.
export const meetingStructureLabel = (row) => {
    if (!row.isLead) return '';
    return row.security?.raw?.structureLabel
        || (row.start ? row.start.replace(/→/g, ' → ') : '');
};

export const traderCode = (entry) => entry?.Company?.name?.slice(0, 3).toUpperCase() || '';

// Outright/fwd single-leg tenors show upper-case ("6M"); a spread/fly keeps its
// lower-case legs ("6m x 1y"), detected by the " x " join.
const upperTenor = (t) => (t && !t.includes('x') ? t.toUpperCase() : (t ?? ''));

const sizeLabel = (entry) => {
    if (!entry) return '';
    const raw = entry.dv01 ?? entry.size;
    return raw === undefined || raw === null || raw === '' ? '' : `${raw}`;
};

// One security expands into as many rows as it has price depth. A security with
// no prices still gets one row, so its start/tenor shows and the empty cells stay
// clickable for price entry.
export const buildIrsRows = (securities = [], minRowsPerSecurity = 1, serverTime, isBrokerView = false, selectedTraderId = null, endedWindowMs = null, liveOnly = false) => securities
    .flatMap((security) => {
        const bids = security?.Bid || [];
        const offers = security?.Offer || [];
        // Trader view stacks one row per broker session on the same structure;
        // broker view has 0 or 1 entries. Live sessions, then ones ended within the
        // window, else the structure's System mid on its first row.
        const systemMid = security?.raw?.systemMid ?? null;
        const midOptions = { serverTime, endedWindowMs, liveOnly };
        const midStack = resolveMidRows({ midStack: security?.midStack || [], systemMid, ...midOptions });
        // A trader gets one extra empty row under the sessions to start one with another broker.
        const hasSession = midStack.some((entry) => entry.midState !== MID_STATE.SYSTEM);
        const midRows = midStack.length + (!isBrokerView && hasSession ? 1 : 0);
        const depth = Math.max(bids.length, offers.length, midRows, minRowsPerSecurity);
        return Array.from({ length: depth }, (_, index) => {
            const rawMid = midStack[index] || null;
            // A System or Ended mid is not a session anyone can act on: no interest, no dots,
            // no session id — it only shows a price.
            const live = isLiveMidState(rawMid?.midState);
            // Broker view (AGY + IDB): overlay the selected trader's interest
            // onto the mid entry so the row reflects that trader (not the
            // broker), and swaps as the chip changes. Kept in sync live by the
            // `irs-matching-participant` socket event → applyParticipantUpdate.
            let mid = live ? rawMid : null;
            if (mid && isBrokerView) {
                const participant = selectedTraderId
                    ? (rawMid.participants || []).find((p) => p.traderId === selectedTraderId)
                    : null;
                const buy = participant?.buy ?? 0;
                const sell = participant?.sell ?? 0;
                mid = {
                    ...rawMid,
                    buy,
                    sell,
                    // iet is per-user (userIrsMatching.expiresAt) — the broker
                    // hasn't got their own userMatching in the on-behalf flow, so
                    // rawMid.iet is null. Take the overlaid trader's iet instead
                    // so the Interest Validity countdown renders.
                    iet: participant?.iet ?? rawMid.iet ?? null,
                    myInterest: (Number(buy) || 0) > 0 || (Number(sell) || 0) > 0
                };
            }
            const hasContent = Boolean(bids[index] || offers[index] || rawMid);
            const isLead = index === 0;
            return {
                id: `${security?.id}_${index}`,
                isLead,
                hasContent,
                isGroupEnd: index === depth - 1,
                bidItem: bids[index] || null,
                offerItem: offers[index] || null,
                security,
                start: isLead ? security?.start ?? '' : '',
                // Single-leg (outright / fwd-start) tenors render upper-case ("6M"),
                // matching the design; spreads/flies keep their lower-case "6m x 1y".
                tenor: isLead ? upperTenor(security?.tenor) : '',
                bidTrader: traderCode(bids[index]),
                bidSize: sizeLabel(bids[index]),
                bid: bids[index]?.price ?? '',
                offer: offers[index]?.price ?? '',
                offerSize: sizeLabel(offers[index]),
                offerTrader: traderCode(offers[index]),
                // 0 = no interest — render blank, same as null. `??` would keep
                // 0 through and the cell would show "0" via renderCellContent's
                // fallthrough (React prints 0 unlike '' / null).
                buy: mid?.buy || '',
                midId: mid?.id ?? null,
                midEndsAt: mid?.endsAt ?? null,
                iet: mid?.iet ?? null,
                mid: rawMid?.mid ?? '',
                midState: rawMid?.midState ?? null,
                midEndedAt: rawMid?.midState === MID_STATE.ENDED ? (rawMid.endedAt ?? rawMid.endsAt ?? null) : null,
                midStartedAt: rawMid?.midState === MID_STATE.ENDED ? (rawMid.startedAt ?? null) : null,
                midBroker: rawMid?.bkr ?? '',
                midInterest: mid?.interest ?? null,
                sell: mid?.sell || '',
                myInterest: Boolean(mid?.myInterest),
                liveInterest: Boolean(mid?.liveInterest),
                // Trade-flash coordinates (see IrsMatchingGrid.PriceRenderer): tt is
                // the trade time, tInterest true while any trade has happened on the
                // session — the grid paints the row red for the 120s trade window.
                midTt: mid?.tt ?? null,
                midTInterest: Boolean(mid?.tInterest),
                // Row-level party (for the mid session on this row), used by the
                // grid's chip-focus dimming.
                midCompanyId: mid?.companyId ?? null
            };
        });
    });
